import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { Select } from '@/lib/base/forms/select/select';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { useFieldType } from '@/lib/crm/hooks/useFieldTypes';
import { logger } from '@/lib/logger';
import type { QuantityValue } from '@/types/crm';

import type { PropertyInputProps } from './TextInput';

export interface CurrencyInputBaseProps {
  value: QuantityValue | null;
  onChange: (value: QuantityValue | null) => void;
  units: Array<{ value: string; label: string }>;
  defaultUnit?: string;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Field size on the shared library scale. Defaults to 'sm'. */
  size?: SelectSize;
}

function resolveDefaultUnit(
  defaultUnit: string | undefined,
  units: Array<{ value: string; label: string }>
): string {
  if (defaultUnit && units.some(u => u.value === defaultUnit)) {
    return defaultUnit;
  }
  return units[0]?.value ?? '';
}

/**
 * Get currency symbol from currency code using Intl API
 */
function getCurrencySymbol(currencyCode: string): string {
  try {
    // Format 0 to get just the symbol
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0);
    // Extract just the symbol (remove digits and spaces)
    return formatted.replace(/[\d\s,]/g, '').trim();
  } catch {
    // Fallback if currency code is invalid
    return '$';
  }
}

/**
 * Get the fraction-digit count for a currency code (0 for JPY/KRW, 2 for most,
 * 3 for e.g. BHD). Falls back to 2 when the code is empty or unrecognized.
 */
function getCurrencyFractionDigits(currencyCode: string): number {
  try {
    return (
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    // Fallback if currency code is empty or invalid
    return 2;
  }
}

/**
 * CurrencyInputBase - Base component for currency values (amount + unit).
 */
export function CurrencyInputBase({
  value,
  onChange,
  units,
  defaultUnit,
  disabled = false,
  placeholder = '0.00',
  ariaLabel,
  inputRef,
  size = 'sm',
}: CurrencyInputBaseProps) {
  // Track whether the input is focused — show raw number when editing, formatted when blurred
  const [isFocused, setIsFocused] = useState(false);
  // Local edit string — only used while focused to preserve user's exact input
  const [editValue, setEditValue] = useState('');
  const internalRef = useRef<HTMLInputElement | null>(null);
  const effectiveRef = inputRef ?? internalRef;

  const resolvedDefaultUnit = resolveDefaultUnit(defaultUnit, units);

  // Track selected unit independently to preserve it when amount is cleared
  const [selectedUnit, setSelectedUnit] = useState(
    value?.unit || resolvedDefaultUnit || ''
  );

  // Update selected unit when value changes from outside
  useEffect(() => {
    if (value?.unit) {
      setSelectedUnit(value.unit);
    }
  }, [value?.unit]);

  // Adopt a late-arriving org default. `units` and the org both load
  // asynchronously, so a cold form can render, fall back to `units[0]` (USD),
  // and only then see the org's default currency arrive — leaving the picker
  // on USD forever, since the revalidation effect below only repairs units
  // that are *invalid*. Re-seed when the resolved default changes, but only
  // while the selection is still the previous default-derived fallback: a unit
  // the user picked, or one that came from a stored value, always wins.
  const previousDefaultUnitRef = useRef(resolvedDefaultUnit);
  useEffect(() => {
    const previousDefaultUnit = previousDefaultUnitRef.current;
    previousDefaultUnitRef.current = resolvedDefaultUnit;
    if (!resolvedDefaultUnit || resolvedDefaultUnit === previousDefaultUnit) {
      return;
    }
    if (value?.unit) {
      return;
    }
    if (selectedUnit && selectedUnit !== previousDefaultUnit) {
      return;
    }
    setSelectedUnit(resolvedDefaultUnit);
  }, [resolvedDefaultUnit, selectedUnit, value?.unit]);

  // Re-validate selectedUnit when units prop changes
  useEffect(() => {
    const unitIsValid = units.some(u => u.value === selectedUnit);
    if (!unitIsValid && units.length > 0) {
      const newUnit = resolvedDefaultUnit;
      setSelectedUnit(newUnit);
      if (value?.amount !== undefined && value?.amount !== null) {
        const parsed =
          typeof value.amount === 'string'
            ? parseFloat(value.amount)
            : value.amount;
        if (!isNaN(parsed)) {
          onChange({ amount: parsed, unit: newUnit });
        }
      }
    }
  }, [units, selectedUnit, value?.amount, onChange, resolvedDefaultUnit]);

  // Repair `value.unit` when units are known but the parent's stored unit is
  // empty/invalid. This covers a specific race:
  //
  //   1. `units` hasn't loaded yet (useFieldTypes initial fetch in flight)
  //   2. The user (or an e2e `fill`) types an amount → `handleAmountChange`
  //      emits `onChange({ amount, unit: '' })` (selectedUnit & units[0] are
  //      both empty) — the parent form now holds an invalid quantity
  //   3. `units` loads → the effect above runs and re-emits onChange with the
  //      valid unit, ONLY IF `selectedUnit` is still invalid at that point.
  //
  // The problem is the parent form's state isn't fully observable here: in
  // practice we still see `value.unit === ''` after the parent applied step 2,
  // even though the `selectedUnit`-driven effect above sees `selectedUnit ==
  // 'USD'` and exits. So we add a second, complementary effect that watches
  // the *value* directly and re-emits whenever value has an amount but no
  // valid unit and we know what unit to use. Idempotent — the parent re-emits
  // back through `value`, but the guards make this a one-shot correction.
  useEffect(() => {
    if (
      value?.amount !== undefined &&
      value?.amount !== null &&
      (!value.unit || !units.some(u => u.value === value.unit)) &&
      units.length > 0
    ) {
      const parsed =
        typeof value.amount === 'string'
          ? parseFloat(value.amount)
          : value.amount;
      if (!isNaN(parsed)) {
        onChange({ amount: parsed, unit: resolvedDefaultUnit });
      }
    }
  }, [value, units, onChange, resolvedDefaultUnit]);

  // Fraction digits for the selected currency (0 for JPY/KRW, 2 for most).
  // Drives display formatting and input validation below.
  const fractionDigits = useMemo(
    () => getCurrencyFractionDigits(selectedUnit),
    [selectedUnit]
  );

  // Format amount for display
  const displayAmount = (() => {
    // While editing, show the user's exact input string
    if (isFocused) {
      return editValue;
    }
    // When blurred, format from the canonical value
    if (value?.amount === undefined || value?.amount === null) {
      return '';
    }
    const num =
      typeof value.amount === 'string'
        ? parseFloat(value.amount)
        : value.amount;
    if (isNaN(num)) {
      return '';
    }
    try {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(num);
    } catch {
      return String(num);
    }
  })();

  const handleAmountChange = useCallback(
    (newValue: string) => {
      // Strip formatting commas (from paste or residual formatting)
      const cleaned = newValue.replace(/,/g, '');

      // Allow empty → clear
      if (!cleaned) {
        setEditValue('');
        onChange(null);
        return;
      }

      // Validate input: optional minus, digits, optional single decimal with
      // up to the selected currency's fraction digits (none for zero-decimal
      // currencies like JPY/KRW). Allow partial states like "12." while typing.
      const amountPattern =
        fractionDigits > 0
          ? new RegExp(`^-?\\d*\\.?\\d{0,${fractionDigits}}$`)
          : /^-?\d*$/;
      if (!amountPattern.test(cleaned)) {
        return; // reject invalid input (e.g., too many decimal places, multiple dots)
      }

      setEditValue(cleaned);

      // Only update parent when we have a valid number (not just "-" or "12.")
      const parsed = parseFloat(cleaned);
      if (!isNaN(parsed)) {
        // `selectedUnit` is initialized from `value?.unit || units[0]?.value`
        // but `units` is loaded asynchronously from useFieldTypes — on a cold
        // form open, `units` is `[]` during the first render so `selectedUnit`
        // is `''`. The revalidation effect (above) repairs selectedUnit when
        // `units` arrives, but if the user (or an e2e fill) types an amount
        // BEFORE that effect runs, onChange would emit `unit: ''` and the
        // server rejects with `'Amount' unit must not be empty or whitespace`.
        // Fall back to the first available unit at the point of emission so
        // a valid quantity is always produced once units are known.
        const unit = selectedUnit || resolvedDefaultUnit || '';
        onChange({ amount: parsed, unit });
      }
    },
    [onChange, selectedUnit, resolvedDefaultUnit, fractionDigits]
  );

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      setSelectedUnit(newUnit);
      if (value?.amount === undefined || value?.amount === null) {
        onChange(null);
      } else {
        const parsed =
          typeof value.amount === 'string'
            ? parseFloat(value.amount)
            : value.amount;
        onChange(isNaN(parsed) ? null : { amount: parsed, unit: newUnit });
      }
    },
    [onChange, value?.amount]
  );

  // Items for the compact trailing select. The item objects carry ONLY the
  // code as label — the trigger renders the selected item's label (and
  // supportingText, when present), and the whole point of the compact
  // trailing select is a trigger that reads just "USD ⌄". The popover rows
  // get the symbol + full currency name via the meta lookup below instead.
  const selectItems = useMemo(
    () => units.map(u => ({ id: u.value, label: u.value })),
    [units]
  );

  // symbol + full name per code, for the popover rows.
  const unitMeta = useMemo(
    () =>
      new Map(
        units.map(u => [
          u.value,
          { name: u.label, symbol: getCurrencySymbol(u.value) },
        ])
      ),
    [units]
  );

  const handleUnitSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (key !== null && key !== undefined) {
        handleUnitChange(String(key));
      }
    },
    [handleUnitChange]
  );

  const handleFocus = useCallback(() => {
    // Seed edit value from the canonical amount — whole numbers have no decimals
    if (value?.amount !== undefined && value?.amount !== null) {
      const num =
        typeof value.amount === 'string'
          ? parseFloat(value.amount)
          : value.amount;
      if (!isNaN(num)) {
        // Show clean number: 1000 → "1000", 1000.5 → "1000.50" (per-currency digits)
        setEditValue(
          Number.isInteger(num) ? String(num) : num.toFixed(fractionDigits)
        );
      } else {
        setEditValue('');
      }
    } else {
      setEditValue('');
    }
    setIsFocused(true);
  }, [value?.amount, fractionDigits]);

  const handleBlur = useCallback(() => setIsFocused(false), []);

  // Always show currency symbol when unit is selected. Memoized on the unit —
  // getCurrencySymbol allocates an Intl.NumberFormat, so don't re-run it on
  // every keystroke.
  const currencySymbol = useMemo(
    () => (selectedUnit ? getCurrencySymbol(selectedUnit) : undefined),
    [selectedUnit]
  );

  return (
    // One joined field — the design system's currency-input pattern: symbol
    // prefix, amount, and a compact trailing native currency select showing
    // just the code (USD ⌄). Replaces the old two-box layout (amount input +
    // separate 176px searchable picker), which dominated every amount field
    // across the product. The wide searchable CurrencyPickerControl remains
    // for the workspace settings page, where a full-width field is right.
    // A money field never needs full width — cap it, but let it shrink
    // inside narrower form columns.
    <InputGroup
      aria-label={ariaLabel || 'Currency amount'}
      prefix={currencySymbol}
      value={displayAmount}
      onChange={handleAmountChange}
      isDisabled={disabled}
      size={size}
      className='max-w-md'
      trailingAddon={
        // Styled library Select, NOT a native <select>: the native popup is
        // rendered by the OS (system dark theme, system font scaling) and
        // cannot match the design system — on macOS it shows as a giant
        // dark list over a light page. The library popover is ours to style
        // and coordinates with the modal overlay stack. Trigger drops its
        // own chrome to sit embedded inside the group's border.
        <Select
          aria-label='Currency'
          size={size}
          items={selectItems}
          selectedKey={selectedUnit || null}
          onSelectionChange={handleUnitSelectionChange}
          isDisabled={disabled}
          className='h-full w-auto'
          triggerClassName='h-full w-auto rounded-l-none bg-transparent shadow-none ring-0'
          popoverClassName='min-w-56'
          popoverPlacement='bottom end'
        >
          {item => (
            <Select.Item
              id={item.id}
              label={item.label}
              supportingText={unitMeta.get(item.id)?.name}
              icon={
                <span
                  aria-hidden
                  // w-8, not w-5: three-character symbols (CHF, HK$, MX$)
                  // overflow a 20px box; fixed width keeps codes aligned.
                  className='w-8 shrink-0 text-center text-tertiary'
                >
                  {unitMeta.get(item.id)?.symbol}
                </span>
              }
            />
          )}
        </Select>
      }
    >
      <InputBase
        type='text'
        inputMode='decimal'
        placeholder={placeholder}
        size={size}
        ref={effectiveRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </InputGroup>
  );
}

/**
 * Format a currency value for display.
 * Always formats as currency using Intl.NumberFormat.
 *
 * @param value - The currency value to format
 * @returns Formatted string representation
 */
// eslint-disable-next-line react-refresh/only-export-components -- utility function co-located with CurrencyInput component
export function formatCurrency(value: QuantityValue | null): string {
  if (!value || value.amount === undefined || value.amount === null) {
    return '';
  }

  // Convert string amounts to number (backend sends Decimal as string)
  const numAmount =
    typeof value.amount === 'string' ? parseFloat(value.amount) : value.amount;

  if (value.unit) {
    try {
      // No fraction-digit override: Intl applies the currency-correct digit
      // count automatically (2 for USD, 0 for JPY/KRW, 3 for BHD, ...).
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: value.unit,
      }).format(numAmount);
    } catch {
      // Fallback if currency code is invalid
      return `${numAmount.toLocaleString()} ${value.unit}`;
    }
  }

  // Fallback for values without unit
  return numAmount.toLocaleString();
}

/**
 * CurrencyInput - PropertyInput adapter for currency values.
 * Wraps CurrencyInputBase with propertyType interface.
 */
export const CurrencyInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  size = 'sm',
}) => {
  const fieldType = useFieldType(propertyType.field_type_name);
  const units = fieldType?.units || [];
  const { currentOrg } = useOrg();
  const orgDefaultUnit = currentOrg?.defaultCurrency ?? undefined;

  // Coerce string/number values to QuantityValue format
  let currencyValue: QuantityValue | null = null;
  if (
    typeof value === 'object' &&
    value !== null &&
    'amount' in value &&
    'unit' in value &&
    typeof value.unit === 'string'
  ) {
    currencyValue = value as QuantityValue;
  } else if (typeof value === 'string' || typeof value === 'number') {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (!isNaN(parsed) && units.length > 0) {
      currencyValue = {
        amount: parsed,
        unit: resolveDefaultUnit(orgDefaultUnit, units),
      };
    } else if (units.length === 0) {
      logger.warn('[CurrencyInput] Cannot coerce value - no units available', {
        value,
        propertyTypeName: propertyType?.property_type_name,
        component: 'CurrencyInput',
      });
    }
  }

  return (
    <CurrencyInputBase
      value={currencyValue}
      onChange={onChange}
      units={units}
      defaultUnit={orgDefaultUnit}
      disabled={disabled}
      ariaLabel={propertyType.label}
      placeholder={placeholder}
      inputRef={inputRef}
      size={size}
    />
  );
};
